import React, {useEffect, useState} from 'react';
import * as _ from 'lodash';
import moment from 'moment';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {user} from '../../../../store/reducers/user';
import Formatter from '../../../../util/formatter';
import useOpenState from '../../../../hooks/useOpenState';
import Notes from '../../../../util/notes';
import PropertyList from './propertyList';
import MessageBox from '../../../../util/messageBox';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import {setSnackbar} from '../../../../store/reducers/globals';

const propertyListInitialState = () => ({
    medications: {
        id: 0,
        question: 'Medications?',
        name: 'medications',
        checked: null,
        comment: '',
        checkedBy: '',
        checkedDate: null,
    },
    keys: {
        id: 1,
        question: 'Keys?',
        name: 'keys',
        checked: null,
        comment: '',
        checkedBy: '',
        checkedDate: null,
    },
    handbag: {
        id: 2,
        question: 'Handbag?',
        name: 'handbag',
        checked: null,
        comment: '',
        checkedBy: '',
        checkedDate: null,
    },
    purse: {
        id: 3,
        question: 'Purse/Wallet/Money?',
        name: 'purse/wallet/money',
        checked: null,
        comment: '',
        checkedBy: '',
        checkedDate: null,
    },
    spectacles: {
        id: 4,
        question: 'Spectacles?',
        name: 'spectacles',
        checked: null,
        comment: '',
        checkedBy: '',
        checkedDate: null,
    },
    hearingAids: {
        id: 5,
        question: 'Hearing Aids?',
        name: 'hearing aids',
        checked: null,
        comment: '',
        checkedBy: '',
        checkedDate: null,
        checkedOption: null,
        options: [
            {id: 0, name: 'Left', value: 'left'},
            {id: 1, name: 'Right', value: 'right'},
            {id: 2, name: 'Both', value: 'both'},
        ],
    },
    wristwatch: {
        id: 6,
        question: 'Wristwatch?',
        name: 'wristwatch',
        checked: null,
        comment: '',
        checkedBy: '',
        checkedDate: null,
    },
    dentures: {
        id: 7,
        question: 'Dentures?',
        name: 'dentures',
        checked: null,
        comment: '',
        checkedBy: '',
        checkedDate: null,
        checkedOption: null,
        options: [
            {id: 0, name: 'Top', value: 'top'},
            {id: 1, name: 'Bottom', value: 'bottom'},
            {id: 2, name: 'Both', value: 'both'},
        ],
    },
    suitcase: {
        id: 8,
        question: 'Suitcase/Holdall?',
        name: 'suitcase/holdall',
        checked: null,
        comment: '',
        checkedBy: '',
        checkedDate: null,
    },
    walkingAid: {
        id: 9,
        question: 'Walking Aid?',
        name: 'walking aid',
        checked: null,
        comment: '',
        checkedBy: '',
        checkedDate: null,
    },
    jewellery: {
        id: 10,
        question: 'Jewellery?',
        name: 'jewellery',
        checked: null,
        comment: '',
        checkedBy: '',
        checkedDate: null,
    },
    additional: {
        id: 11,
        question: 'Additional Items?',
        name: 'additional items',
        checked: null,
        comment: '',
        checkedBy: '',
        checkedDate: null,
    },
    cannotAccept: {
        id: 12,
        question:
            'Patient understands that the trust cannot accept responsibility for any items left in the department?',
        name: 'responsibility',
        checked: null,
        comment: '',
        checkedBy: '',
        checkedDate: null,
    },
});

const initFirstStepData = () => ({
    when: moment(),
    undressedIntoGown: {
        value: 'notRecorded',
        comment: '',
        checkedBy: '',
        checkedDate: null,
    },
    namebands: {
        value: 'notRecorded',
        comment: '',
        checkedBy: '',
        checkedDate: null,
    },
    // painScoreAssessed: null,
    analgesia: {
        value: 'notRecorded',
        comment: '',
        analgesiaRefused: false,
        checkedBy: '',
        checkedDate: null,
    },
});

const initialFormData = () => ({
    ...initFirstStepData(),
    propertyList: propertyListInitialState(),
});

export default function NursingAdmission(props) {
    const {t} = useTranslation();
    const userObject = useSelector(user);
    const dispatch = useDispatch();

    const {disableSave} = props;

    const [formData, setFormData] = useState(props.patient?.studies?.nursingAdmission || initialFormData());
    const [isClearOpen, openClear, closeClear] = useOpenState(false, true);

    useEffect(() => {
        if (props.onCustomButtons) {
            props.onCustomButtons([
                <AgyleButton
                    key="clear"
                    onClick={openClear}
                    variant="contained"
                    disabled={disableSave}
                >
                    {t('Clear')}
                </AgyleButton>,
            ]);
        }
        if (props.onSubmitFunction) {
            props.onSubmitFunction(handleSaveNursingAdmission);
        }
        if (props.onDisableSubmit) {
            props.onDisableSubmit(checkRequiredFields() || disableSave);
        }
    }, [props.patient, formData, disableSave]);

    const clearForm = () => {
        setFormData(initialFormData());
        closeClear();
    };

    const handleSaveNursingAdmission = () => {
        const areObjectsEqual = _.isEqual(
            formData,
            props.patient?.studies?.nursingAdmission,
        );

        if (areObjectsEqual) {
            if (props._onForceClose) {
                dispatch(setSnackbar({type: 'info', message: t('No changes to the form have been detected.')}));
                props._onForceClose();

            }
            return;
        }
        const formDataToSave = {
            ...formData,
            lastUpdateDate: moment().toISOString(),
            lastUpdateBy: Formatter.formatUserFullName(userObject),
        };
        const dataToSave = [
            {
                name: 'studies.nursingAdmission',
                value: formDataToSave,
                noteToAdd: Notes.getNursingAdmissionNote(formDataToSave),
                noteType: 'propertyList'
            },
        ];

        props.onChange(dataToSave);
    };

    const checkRequiredFields = () => {
        return (formData?.propertyList?.cannotAccept.checked === false && !formData?.propertyList?.cannotAccept.comment) || _.isNull(formData?.propertyList?.cannotAccept.checked);
    };

    return (
        <>
            <PropertyList
                {...props}
                data={formData}
                setData={setFormData}
                disableSave={disableSave}
            />

            <MessageBox
                open={isClearOpen}
                onClose={closeClear}
                title={t('Clear Form')}
                haveAccept={{yes: t('Submit'), no: t('Close')}}
                onAccept={clearForm}
                message={t('Are you sure you want to clear the form?')}
            />
        </>
    );
}
